import {
  ArrowRightOutlined,
  ExclamationCircleOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { Flex, Tooltip } from 'antd';
import Button from 'components/Button';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import Table from 'components/Table';
import { StatusTag } from './StatusTag';

import {
  shipmentStatusMap,
  statusMap,
} from 'screens/deliveries/package-tracker/helper';
import { renderDate, renderInfoValue } from 'utils/helpers';

import useMediaQuery from 'hooks/useMediaQuery';

import {
  findAllStuckPackages,
  getStatusAge,
} from 'screens/deliveries/package-tracker/util';

import type { ColumnsType, TableProps } from 'antd/es/table';
import {
  PackageDTO,
  PackageListDTO,
} from 'screens/deliveries/package-tracker/types';
import { useAnalytics } from 'context/AnalyticsContext';
import useTrackerWrapper from 'hooks/useTracker';

interface PackageTableProps {
  data: PackageListDTO;
  isLoading: boolean;
  isViewingStuckPackages: boolean;
  pagination: TableProps['pagination'];
  onPackageRowClick?: (data: PackageDTO) => void;
  highlight?: string;
}

const PackageTable: React.FC<PackageTableProps> = ({
  data,
  isLoading,
  // isViewingStuckPackages,
  highlight,
  pagination,
  onPackageRowClick,
}: PackageTableProps) => {
  const { t } = useTranslation();
  const { onClickWrapper } = useTrackerWrapper();

  const stuckPackagesMap = useMemo(() => {
    return findAllStuckPackages(data);
  }, [data]);

  const isMobileScreenSize = useMediaQuery('(max-width: 576px)');

  const handleClickDetail = (data: PackageDTO) => {
    onClickWrapper('Package Detail', () => onPackageRowClick?.(data));
  };

  const packageColumns: ColumnsType<PackageDTO> = useMemo(
    () => [
      {
        title: t('packageTrackerDashboard.table.packageNumber'),
        dataIndex: 'packageNumber',
        key: 'packageNumber',
        width: 200,
        onCell: (data) => ({ onClick: () => handleClickDetail(data) }),
        className: 'clickable',
        render: (text) => (
          <Flex justify="space-between" align="center" gap={8}>
            {stuckPackagesMap?.[text] ? (
              <Flex align="center" gap={8}>
                {renderInfoValue(text, 'N/A')}
                <Tooltip
                  className="clickable"
                  placement={'top'}
                  title={t(
                    'packageTrackerDashboard.tooltips.stuckPackageDaysInfo',
                    {
                      days: stuckPackagesMap?.[text],
                    },
                  )}
                >
                  <WarningOutlined
                    style={{ color: 'indianred', fontSize: '16px' }}
                  />
                </Tooltip>
              </Flex>
            ) : (
              renderInfoValue(text, 'N/A')
            )}
            {!isMobileScreenSize ? (
              <Button className="table-click-indicator" type="text">
                <ArrowRightOutlined />
              </Button>
            ) : null}
          </Flex>
        ),
      },
      {
        title: t('packageTrackerDashboard.table.packageStatus'),
        dataIndex: 'status',
        width: 220,
        key: 'status',
        render: (status, record) => (
          <StatusTag
            status={record?.invoicePrimaryId ? 'fulfilled' : status}
            statusConfigMap={statusMap}
          />
        ),
      },
      {
        title: () => (
          <>
            <Flex align="center" gap={10}>
              <span>{t('packageTrackerDashboard.table.statusAge')}</span>
              <Tooltip title={t('packageTrackerDashboard.tooltips.statusAge')}>
                <ExclamationCircleOutlined style={{ color: '#000' }} />
              </Tooltip>
            </Flex>
          </>
        ),
        dataIndex: 'packageNumber',
        key: 'statusAge',
        width: 150,
        render: (_, record) => {
          const statusAge = getStatusAge(record);
          return statusAge
            ? renderInfoValue(statusAge + ' days', 'N/A')
            : 'N/A';
        },
      },
      {
        title: t('packageTrackerDashboard.table.soNumber'),
        dataIndex: ['salesOrder', 'orderNumber'],
        width: 150,
        key: 'salesOrderNumber',
        render: (text) => {
          return renderInfoValue(text, 'N/A');
        },
        className: 'clickable',
      },
      {
        title: t('packageTrackerDashboard.table.shipmentStatus'),
        dataIndex: ['salesOrder', 'status'],
        key: 'salesOrderStatus',
        width: 220,

        render: (status) => {
          const config =
            shipmentStatusMap?.[
              status?.toLowerCase() as keyof typeof shipmentStatusMap
            ];
          return (
            <Flex gap={6} align="center">
              <span>{config?.label || 'N/A'}</span>
              <Tooltip title={config?.tooltip || 'N/A'}>{config?.icon}</Tooltip>
            </Flex>
          );
        },
        className: 'clickable',
      },
      {
        title: t('packageTrackerDashboard.table.agronomist'),
        dataIndex: ['agronomist', 'name'],
        key: 'agronomistName',
        width: 200,
        render: (text) => renderInfoValue(text, 'N/A'),
        className: 'clickable',
      },
      {
        title: t('packageTrackerDashboard.table.farmerGroup'),
        dataIndex: ['farmerGroup', 'name'],
        key: 'farmerGroupName',
        render: (text) => renderInfoValue(text, 'N/A'),
        className: 'clickable',
      },
      {
        title: t('packageTrackerDashboard.table.shippedOn'),
        width: 150,
        dataIndex: 'packageDate',
        key: 'packageDate',
        render: (date) => renderDate(date, 'N/A'),
      },
      {
        title: t('packageTrackerDashboard.table.carrier'),
        dataIndex: 'carrier',
        width: 150,
        key: 'carrier',
        render: (text) => renderInfoValue(text, 'N/A'),
      },
      {
        title: t('packageTrackerDashboard.table.deliveredOn'),
        dataIndex: 'shipmentDeliveredDate',
        width: 150,
        key: 'shipmentDeliveredDate',
        render: (date) => renderDate(date, 'N/A'),
      },
    ],
    [stuckPackagesMap, t],
  );

  const getRowClassName = (record: PackageDTO) => {
    if (record?.id === highlight) {
      return 'table-highlight table-highlight__gray';
    }
    if (record?.packageNumber && stuckPackagesMap?.[record?.packageNumber]) {
      return 'stuck-package rounded';
    }
    return '';
  };

  return (
    <Table<PackageDTO>
      loading={isLoading}
      tableHeader={packageColumns}
      tableColumns={data}
      rowKey="packageId"
      pagination={pagination}
      style={{ marginBottom: 20 }}
      rowClassName={getRowClassName}
      highlightColor="gray"
    />
  );
};

export default PackageTable;
